/* eslint-disable react/no-unescaped-entities */
import {
  Sun,
  Plug,
  BatteryCharging,
  Cpu,
  Zap,
} from "lucide-react";

export const services = [
  {
    id: "full-installations",
    slug: "full-installations",
    title: "Full Solar Installations",
    short:
      "End-to-end solar system design and rooftop installation for homes, farms and businesses across New Plymouth and Taranaki.",
    icon: Sun,
    image:
      "/kaitake-ranges.png",
    eyebrow: "Turnkey solar",
    headline: "A complete solar system, installed by people who actually care.",
    intro:
      "From the first conversation through to switch-on, our team handles every step: roof inspection, system design, panel + inverter + battery selection, council and lines-company paperwork, installation, and commissioning. You get a beautiful, code-compliant solar array — and a power bill that stops being a problem.",
    highlights: [
      {
        h: "Premium panels",
        d: "Tier-1 panels (LONGi, REC, Trina) with 25-year performance warranties.",
      },
      {
        h: "Designed for your roof",
        d: "We model shading, tilt and orientation so every kWh counts on your home.",
      },
      {
        h: "Tidy install",
        d: "Hidden cabling, flush mounts, neat conduit runs. We treat your roof like our own.",
      },
      {
        h: "Single point of contact",
        d: "One project manager from quote to switch-on. No subcontractor runaround.",
      },
    ],
    process: [
      "Free on-site assessment",
      "System design + transparent quote",
      "Council & lines-company paperwork",
      "Typical installation time is within 1 week",
      "Commission, test, switch on",
    ],
    faqs: [
      {
        q: "How long does an install take?",
        a: "Typical installation time is within 1 week, with grid connection following in 1–3 weeks depending on Powerco's lead times.",
      },
      {
        q: "What size system do I need?",
        a: "Most Taranaki homes land in the 5–8 kW range. We size your system based on your power bills, daytime usage and future plans (EV, battery, hot water).",
      },
    ],
  },
  {
    id: "grid-connections",
    slug: "grid-connections",
    title: "Grid Connections",
    short:
      "Certified network applications, metering and lines-company sign-off — handled for you.",
    icon: Plug,
    image:
      "https://images.unsplash.com/flagged/photo-1566838616631-f2618f74a6a2?crop=entropy&cs=srgb&fm=jpg&w=2000&q=80",
    eyebrow: "Network & metering",
    headline: "We talk to the lines company so you don't have to.",
    intro:
      "Connecting solar to the local grid involves multiple parties — Powerco (our Taranaki lines company), your electricity retailer and a registered electrician. Central Solar manages the whole approval chain: ICP applications, meter upgrades to import/export, distributed-generation connection forms and certificate of compliance.",
    highlights: [
      {
        h: "Powerco specialists",
        d: "We know the Taranaki distributed-generation process inside and out.",
      },
      {
        h: "Import/export metering",
        d: "We coordinate your retailer to swap meters so your exported power gets credited.",
      },
      {
        h: "Certified electricians",
        d: "All connections signed off by EWRB-registered electricians in line with all legislative requirements.",
      },
      {
        h: "Off-grid & hybrid",
        d: "Rural Taranaki property? We design hybrid or fully off-grid setups with backup generation.",
      },
    ],
    process: [
      "Confirm ICP and lines company",
      "Submit distributed generation connection application",
      "Coordinate meter upgrade with retailer",
      "Electrical install & CoC",
      "Grid energisation",
    ],
    faqs: [
      {
        q: "Will I get paid for exported power?",
        a: "Yes — most NZ retailers offer a buy-back rate. We help you compare plans and pick the best fit before switching on.",
      },
      {
        q: "How long does grid approval take?",
        a: "Powerco approvals in Taranaki typically take 2–6 weeks depending on system size and current workload.",
      },
    ],
  },
  {
    id: "battery-installs",
    slug: "battery-installs",
    title: "Battery Installs",
    short:
      "Tesla Powerwall, Sungrow, BYD, Sig Energy and Fronius battery storage tailored to your usage pattern.",
    icon: BatteryCharging,
    image:
      "https://images.unsplash.com/photo-1597655601841-214a4cfe8b2c?crop=entropy&cs=srgb&fm=jpg&w=2000&q=80",
    eyebrow: "Energy storage",
    headline: "Use your solar after sunset. Stay powered through outages.",
    intro:
      "Batteries let you store the cheap solar power you generate during the day and use it at night, during peak retail-rate windows, or when the grid goes down. We design battery sizing around your real consumption — not a generic spec sheet.",
    highlights: [
      {
        h: "Tesla Powerwall 3",
        d: "13.5 kWh of seamless backup with whole-home protection and grid-forming inverter.",
      },
      {
        h: "Sungrow & BYD",
        d: "Modular battery stacks scaling from 9.6 kWh to 25.6 kWh+ for larger homes.",
      },
      {
        h: "Sig Energy",
        d: "All-in-one Sig Energy storage systems with integrated inverter and clean wall-mount design.",
      },
      {
        h: "Fronius Reserva",
        d: "Austrian-engineered Fronius battery solutions that pair seamlessly with Fronius inverters.",
      },
      {
        h: "Backup-ready",
        d: "Keep lights, fridge and Wi-Fi on automatically during outages.",
      },
      {
        h: "Add to existing solar",
        d: "Already have panels? We retrofit batteries to most existing inverter setups.",
      },
    ],
    process: [
      "Review power usage profile",
      "Recommend battery size & chemistry",
      "Coordinate with inverter",
      "Install & commission",
      "Configure backup circuits",
    ],
    faqs: [
      {
        q: "How long do batteries last?",
        a: "Modern lithium batteries are warranted for 10 years with 70%+ retained capacity. Real-world life is typically 12–15 years.",
      },
      {
        q: "Will it power my whole house in an outage?",
        a: "Depends on size — a Powerwall 3 will run essential loads (fridge, lights, Wi-Fi, comms) for 12–24 hours, and can be configured for whole-home backup.",
      },
    ],
  },
  {
    id: "micro-inverters",
    slug: "micro-inverters",
    title: "Micro Inverters",
    short:
      "Enphase, APsystems and Atmoce micro-inverter setups for shaded or complex roof layouts.",
    icon: Cpu,
    image:
      "https://images.unsplash.com/photo-1465056836041-7f43ac27dcb5?crop=entropy&cs=srgb&fm=jpg&w=2000&q=80",
    eyebrow: "Per-panel optimisation",
    headline: "Every panel doing its own thing — and doing it better.",
    intro:
      "Micro inverters live on the back of each panel, converting DC to AC right at the source. The result: better performance in shade, panel-level monitoring, and a safer low-voltage rooftop. Ideal for tricky roofs with multiple orientations or partial shading from trees, chimneys or neighbouring buildings.",
    highlights: [
      {
        h: "Enphase IQ8",
        d: "World's leading micro inverter platform with grid-forming sunlight backup.",
      },
      {
        h: "Per-panel monitoring",
        d: "See how each panel is performing, in real time, on your phone.",
      },
      {
        h: "Shade-tolerant",
        d: "One shaded panel doesn't drag down the rest of the string.",
      },
      {
        h: "25-year warranty",
        d: "Industry-leading 25-year micro-inverter warranty for true set-and-forget.",
      },
      {
        h: "Atmoce",
        d: "Next-gen Atmoce micro inverters with smart panel-level monitoring and a price-friendly entry point.",
      },
    ],
    process: [
      "Roof shading analysis",
      "Panel + micro-inverter design",
      "Rapid rooftop install",
      "Gateway commissioning",
      "Phone monitoring set-up",
    ],
    faqs: [
      {
        q: "Micro inverter vs string inverter — which is right?",
        a: "Micro inverters win on shaded, multi-pitch or complex roofs. String inverters are usually cheaper for simple, single-orientation arrays. We'll recommend based on your roof.",
      },
      {
        q: "Can I add panels later?",
        a: "Yes — micro inverter systems scale panel-by-panel, making future expansion simple.",
      },
    ],
  },
  {
    id: "inverters",
    slug: "inverters",
    title: "String Inverters",
    short:
      "Fronius, Sungrow, Sig Energy and Tesla — sized, installed and commissioned to NZ standards.",
    icon: Zap,
    image:
      "https://images.unsplash.com/photo-1624397640148-949b1732bb0a?crop=entropy&cs=srgb&fm=jpg&w=2000&q=80",
    eyebrow: "Central conversion",
    headline: "The proven workhorse of NZ solar.",
    intro:
      "String inverters are the most popular choice for straightforward rooftop solar in Taranaki. We install premium European and Asian inverters that are quiet, efficient and built for the long haul — backed by NZ-based service and warranty support.",
    highlights: [
      {
        h: "Fronius Primo & Symo",
        d: "Austrian-made, ultra-reliable, ideal for residential and small commercial.",
      },
      {
        h: "Sungrow hybrid",
        d: "Battery-ready inverters that grow with you when you add storage.",
      },
      {
        h: "Sig Energy",
        d: "Sig Energy hybrid inverters offering excellent efficiency and battery integration out of the box.",
      },
      {
        h: "Tesla Inverter",
        d: "The Tesla solar inverter pairs natively with Powerwall for the smoothest solar + battery experience.",
      },
      {
        h: "Properly sized",
        d: "We size your inverter to match panel output and future expansion plans.",
      },
    ],
    process: [
      "Calculate DC array sizing",
      "Select inverter & topology",
      "Install in cool, shaded location",
      "Wire to AC switchboard",
      "Commission & test",
    ],
    faqs: [
      {
        q: "How long does an inverter last?",
        a: "Most premium inverters carry a 10-year warranty with expected life of 15–20 years.",
      },
      {
        q: "Hybrid or standard?",
        a: "If there's any chance you'll add a battery in the next 5 years, go hybrid. The price difference is small and it saves replacing later.",
      },
    ],
  },
];

export const findService = (slug) => services.find((s) => s.slug === slug);
